// generated by the plop

import { Injectable } from '@angular/core';
import { Selector, State } from '@ngxs/store';
import { AccessState, AccessStateModel, ProfileState, ProfileStateModel, UserModel } from 'shared';
import { PermissionEnum } from 'symbols';
import { STAND_ACCESS_DEFAULT, StandAccessModel } from './stand-access.model';

@State({ name: 'StandAccess' })
@Injectable()
export class StandAccessState {

  /**
   * Generate configs for classroom module depends on a provided user role.
   */
  static generateStandsAccess(roles: PermissionEnum[], user: UserModel): StandAccessModel {
    // Return permissions as for an unknown role if user not verified or not authenticated.
    if (!user) {
      return this.generateRoleConfig(PermissionEnum.UNKNOWN);
    }

    // Apply permissions of each provided role.
    const rolesConfig: StandAccessModel[] = roles.map(role => this.generateRoleConfig(role));

    // Return defined config.
    return this.mergeConfig(rolesConfig);
  }

  /**
   * Define and create config using provided config data.
   */
  static generateRoleConfig(role: PermissionEnum): StandAccessModel {
    switch (role) {
      case PermissionEnum.CAN_READ_RECEIPT_OF_STAND:
        return {
          ...STAND_ACCESS_DEFAULT,
          canReadReceiptOfStand: true
        };
      case PermissionEnum.CAN_WRITE_RECEIPT_OF_STAND:
        return {
          ...STAND_ACCESS_DEFAULT,
          canWriteReceiptOfStand: true
        };

      case PermissionEnum.CAN_READ_STANDS:
        return {
          ...STAND_ACCESS_DEFAULT,
          canReadStands: true,
          canReadEquipmentOfStand: true
        };
      case PermissionEnum.CAN_READ_STANDS_BY_ID:
        return {
          ...STAND_ACCESS_DEFAULT,
          canReadStandsById: true
        };
      case PermissionEnum.CAN_WRITE_STANDS:
        return {
          ...STAND_ACCESS_DEFAULT,
          canWriteStands: true
        };

      case PermissionEnum.CAN_SAVE_FORCE:
        return {
          ...STAND_ACCESS_DEFAULT,
          canSaveForce: true
        };

      case PermissionEnum.CAN_READ_DEBTS:
        return {
          ...STAND_ACCESS_DEFAULT,
          canReadDebts: true
        };
      case PermissionEnum.CAN_WRITE_DEBTS:
        return {
          ...STAND_ACCESS_DEFAULT,
          canWriteDebts: true
        };
      case PermissionEnum.CAN_CHANGE_STAND_STATUS:
        return {
          ...STAND_ACCESS_DEFAULT,
          canChangeStandStatus: true
        };
      case PermissionEnum.SUPER_USER:
        return {
          canReadStandsById: true,
          canReadStands: true,
          canWriteStands: true,
          canReadEquipmentOfStand: true,
          canReadReceiptOfStand: true,
          canWriteReceiptOfStand: true,
          canReadDebts: true,
          canSaveForce: true,
          canWriteDebts: true,
          canChangeStandStatus: true,
          canRemoveTemplate: true
        };
      default: {
        return {
          ...STAND_ACCESS_DEFAULT
        };
      }
    }
  }

  /**
   * Merge provided roles configs (by union rule).
   */
  static mergeConfig(rolesConfig: StandAccessModel[]): StandAccessModel {
    const moduleConfig: StandAccessModel = STAND_ACCESS_DEFAULT;

    for (const config of rolesConfig) {
      moduleConfig.canReadStands = moduleConfig.canReadStands ? true : config.canReadStands;
      moduleConfig.canReadStandsById = moduleConfig.canReadStandsById ? true : config.canReadStandsById;
      moduleConfig.canReadEquipmentOfStand = moduleConfig.canReadEquipmentOfStand ? true : config.canReadEquipmentOfStand;
      moduleConfig.canWriteStands = moduleConfig.canWriteStands ? true : config.canWriteStands;
      moduleConfig.canSaveForce = moduleConfig.canSaveForce ? true : config.canSaveForce;
      moduleConfig.canReadDebts = moduleConfig.canReadDebts ? true : config.canReadDebts;
      moduleConfig.canWriteDebts = moduleConfig.canWriteDebts ? true : config.canWriteDebts;
      moduleConfig.canChangeStandStatus = moduleConfig.canChangeStandStatus ? true : config.canChangeStandStatus;
      moduleConfig.canReadReceiptOfStand = moduleConfig.canReadReceiptOfStand ? true : config.canReadReceiptOfStand;
      moduleConfig.canWriteReceiptOfStand = moduleConfig.canWriteReceiptOfStand ? true : config.canWriteReceiptOfStand;
      moduleConfig.canRemoveTemplate = moduleConfig.canRemoveTemplate ? true : config.canRemoveTemplate;
    }

    return moduleConfig;
  }

  /**
   * Define and create module access rules using provided portal data.
   */
  @Selector([ProfileState, AccessState])
  static accessToStands(profileState: ProfileStateModel, accessState: AccessStateModel): StandAccessModel {
    const userData = profileState.currentUser;
    const roleList = accessState.roles;

    return StandAccessState.generateStandsAccess(roleList, userData);
  }

}


